import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo, useState } from 'react';
import { sampleTypes } from '../../constants/statusOptions';
import { TestSelectionWithCounts } from '../../components/shared/TestSelectionWithCounts';
import { getLocationsByProject } from '../../services/locationService';
import { getSamplesByLocation } from '../../services/sampleService';
import { getTests, type TestType } from '../../services/testService';
import type { Sample } from '../../types/location';

interface SampleRow extends Sample {
  LocationId: string;
}

interface AddSamplesModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (sampleKeys: string[], testKeys: string[], specimenCounts?: Record<string, number>) => void;
  projectKey: string;
  existingSampleKeys: string[];
}

export const AddSamplesModal = ({
  open,
  onClose,
  onAdd,
  projectKey,
  existingSampleKeys,
}: AddSamplesModalProps) => {
  const theme = useTheme();
  const [samples, setSamples] = useState<SampleRow[]>([]);
  const [tests, setTests] = useState<TestType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedTests, setSelectedTests] = useState<Set<string>>(new Set());
  const [specimenCounts, setSpecimenCounts] = useState<Record<string, number>>({});
  const [locationFilter, setLocationFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setSelectedTests(new Set());
    setSpecimenCounts({});
    setLocationFilter('');
    setTypeFilter('');
    setSearch('');

    const load = async () => {
      setLoading(true);
      const [locations, allTests] = await Promise.all([
        getLocationsByProject(projectKey),
        getTests(),
      ]);
      const allSamples: SampleRow[] = [];
      for (const loc of locations) {
        const locSamples = await getSamplesByLocation(loc.LocationKey);
        locSamples.forEach((s) => allSamples.push({ ...s, LocationId: loc.LocationId }));
      }
      setSamples(allSamples);
      setTests(allTests);
      setLoading(false);
    };
    load();
  }, [open, projectKey]);

  // BUG-2026-0185: samples already on the work order used to be EXCLUDED from
  // this list, so a sample "disappeared" once any test was attached to it —
  // making it impossible to add a second test (e.g. BTS) to a sample that
  // already had TCS. We now keep every project sample selectable and merely
  // flag the ones already on this WO. The backend skips duplicate
  // (sample, test) pairs, so re-selecting a sample to attach a NEW test can't
  // create a duplicate of a test it already has.
  const alreadyAddedSet = useMemo(() => new Set(existingSampleKeys), [existingSampleKeys]);

  const available = samples;

  const locationOptions = useMemo(
    () => [...new Set(available.map((s) => s.LocationId))],
    [available],
  );

  const filtered = useMemo(() => {
    return available.filter((s) => {
      if (locationFilter && s.LocationId !== locationFilter) return false;
      if (typeFilter && s.SampleType !== typeFilter) return false;
      if (search && !s.SampleId.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [available, locationFilter, typeFilter, search]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const toggleTest = (testKey: string) => {
    setSelectedTests((prev) => {
      const next = new Set(prev);
      next.has(testKey) ? next.delete(testKey) : next.add(testKey);
      return next;
    });
  };

  const canSubmit = selected.size > 0 && selectedTests.size > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Tests to Work Order</DialogTitle>
      <DialogContent>
        {/* Sample selection */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Location</InputLabel>
            <Select value={locationFilter} label="Location" onChange={(e) => setLocationFilter(e.target.value)}>
              <MenuItem value="">All Locations</MenuItem>
              {locationOptions.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="">All Types</MenuItem>
              {sampleTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" placeholder="Search Sample ID" value={search} onChange={(e) => setSearch(e.target.value)} />
        </Box>

        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: theme.table.headerBg, fontWeight: 600 } }}>
                <TableCell padding="checkbox" />
                <TableCell>Sample ID</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Depth Top</TableCell>
                <TableCell>Depth Base</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    align="center"
                    sx={{ py: 3, color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    {samples.length === 0
                      ? 'This project has no samples yet. Add samples via Locations & Samples.'
                      : 'No samples match the current filters.'}
                  </TableCell>
                </TableRow>
              ) : filtered.map((s) => (
                <TableRow key={s.SampleKey} hover>
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(s.SampleKey)} onChange={() => toggle(s.SampleKey)} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {s.SampleId}
                      {alreadyAddedSet.has(s.SampleKey) && (
                        <Chip
                          label="On this WO"
                          size="small"
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem', color: 'text.secondary' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{s.LocationId}</TableCell>
                  <TableCell>{s.DepthTop ?? ''}</TableCell>
                  <TableCell>{s.DepthBase ?? ''}</TableCell>
                  <TableCell>{s.SampleType}</TableCell>
                  <TableCell>{s.Status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Test selection with specimen counts */}
        <Divider sx={{ my: 2 }} />
        <TestSelectionWithCounts
          tests={tests}
          selectedTests={selectedTests}
          specimenCounts={specimenCounts}
          onToggleTest={toggleTest}
          onCountChange={(testKey, count) => setSpecimenCounts((prev) => ({ ...prev, [testKey]: count }))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => onAdd(Array.from(selected), Array.from(selectedTests), specimenCounts)}
        >
          Add Selected ({selected.size} samples, {selectedTests.size} tests)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
